// Typed client for Services > Channels — providers, the number inventory, and
// the route CRUD. Every call goes to THIS application's server
// (/api/services/channels/*), which is member-gated for reads, tenant-admin
// gated for writes, and hops to the channels service over S2S. The browser
// never talks to channels directly and never names a tenant.
import type { WebchatAppearance } from '@retink/connector-webchat/appearance'
import { api } from './api'

export interface ChannelEndpoint {
  id: string
  identifier: string
  provider: string          // 'web' | 'vonage' | 'acs' | 'http'
  medium: string            // 'chat' | 'sms' | 'voice' | 'api'
  entryFlowId: string | null
  appearance: Partial<WebchatAppearance>
  config: Record<string, unknown>
  isActive: boolean
  lastInboundAt: string | null
  createdAt: string
  updatedAt: string
  /** Server-composed public webhook URL — present on http routes only. */
  webhookUrl?: string
}

export interface PublishedFlow {
  id: string
  name: string
  description: string | null
  version: number
}

// Providers (layer 1) + Numbers/identifiers (layer 3). Routes (the endpoint CRUD
// below) are layer 4; adaptors (layer 2) ride inside each provider status.
export interface AdaptorInfo {
  key: string
  label: string
  medium: string
}
export interface ProviderStatus {
  provider: 'web' | 'vonage' | 'acs' | 'http'
  label: string
  state: 'ready' | 'unconfigured' | 'coming-soon'
  configured: boolean
  adaptors: AdaptorInfo[]
  endpointCount: number
  missingSecretRefs: string[]
  lastVerifiedAt: string | null
}

export interface NumberInventoryItem {
  msisdn: string
  country: string | null
  capabilities: string[]
  assignedEndpointId: string | null
  assignedIdentifier: string | null
}
export interface NumberInventory {
  available: boolean
  reason?: 'not_configured' | 'provider_error'
  numbers: NumberInventoryItem[]
}

export interface CreateEndpointInput {
  identifier: string
  provider: string
  medium: string
  appearance?: Partial<WebchatAppearance>
  config?: Record<string, unknown>
}

// No entryFlowId: a route's flow binding is made by PUBLISHING a flow whose
// trigger names this identifier, never by editing the route.
export interface UpdateEndpointInput {
  identifier?: string
  appearance?: Partial<WebchatAppearance>
  config?: Record<string, unknown>
  isActive?: boolean
}

const BASE = '/api/services/channels'

export const channels = {
  listEndpoints: () => api.get<{ routes: ChannelEndpoint[] }>(`${BASE}/routes`).then((r) => r.routes),
  listFlows: () => api.get<{ flows: PublishedFlow[] }>(`${BASE}/flows`).then((r) => r.flows),
  listProviders: () => api.get<{ providers: ProviderStatus[] }>(`${BASE}/providers`).then((r) => r.providers),
  listNumbers: (provider: string) => api.get<NumberInventory>(`${BASE}/inventory?provider=${encodeURIComponent(provider)}`),
  create: (input: CreateEndpointInput) => api.post<ChannelEndpoint>(`${BASE}/routes`, input),
  update: (id: string, input: UpdateEndpointInput) => api.patch<ChannelEndpoint>(`${BASE}/routes/${id}`, input),
  remove: (id: string) => api.delete<{ ok: boolean }>(`${BASE}/routes/${id}`),
  impact: (id: string) => api.get<{ liveConversations: number }>(`${BASE}/routes/${id}/impact`),
}
